import {Observable, Subject} from 'rxjs';
import {DynamicInputsInterface} from '../interfaces/DynamicInputs.interface';

/**
 * Represents a loaded file. This class contains the file data and additional information.
 */
export class ExcFileItem {

  private _friendlyFileSize: string;

  /**
   * Returns the size in a human readable format like 0.7MB
   */
  get friendlyFileSize(): string {
    if (!this._friendlyFileSize) {
      let i = -1;
      let tmpFileSize = this.size;
      const byteUnits = [' KB', ' MB', ' GB', ' TB', 'PB', 'EB', 'ZB', 'YB'];
      do {
        tmpFileSize = tmpFileSize / 1024;
        i++;
      } while (tmpFileSize > 1024);

      this._friendlyFileSize = Math.max(tmpFileSize, 0.1).toFixed(1) + byteUnits[i];
    }
    return this._friendlyFileSize;
  }

  /**
   * Returns the created at timestamp as ISO String (without time zone consideration)
   */
  get createdAtString(): string {
    return new Date(this.createdAt).toISOString();
  }

  /**
   * Returns this instance as blob
   */
  get asBlob(): Blob {
    return new Blob([this.data], {type: this.type});
  }

  /**
   * A flag that determines if the data of this ExcFileItem is still loading with a FileReader
   */
  public isLoading = false;

  /**
   * Misc data than can be anything ... But per default it is label and description
   */
  public misc: { [key: string]: any };

  public id: string;
  public fkid: string;
  /**
   * Update token
   */
  public upt: string;
  public owner: string;
  /**
   * A timestamp from when the data was uploaded in milliseconds
   */
  public original: any = {};
  public createdAt: number;
  public fullName: string;
  public name: string;
  public extension: string;
  public type: string;
  public AdditionalAudit: number = null;
  public size: number;
  public data: ArrayBuffer;
  private _afterLoading: Subject<ExcFileItem> = new Subject<ExcFileItem>();
  public afterLoading: Observable<ExcFileItem> = this._afterLoading.asObservable();

  /**
   * Creates as ExcFileItem object from an api response object. The dynamicInputs array defines which data
   * from api response data should be saved in misc
   * @param response - data object that provides the source data
   * @param dynamicInputs - data keys for misc data
   */
  public static fromApiResponse(response: any, dynamicInputs: DynamicInputsInterface[]): ExcFileItem {
    const fileItem = new ExcFileItem();
    fileItem.id = response.ID;
    fileItem.fkid = response.FKID;
    fileItem.upt = response.UPT;
    fileItem.owner = response.CRU;
    fileItem.createdAt = response.CRD;

    fileItem.fullName = response.FileName;
    const nameParts = fileItem.fullName.split('.');
    fileItem.extension = nameParts.pop();
    fileItem.name = nameParts.join('.');
    fileItem.type = response.MIME;
    fileItem.size = response.Size;

    fileItem.misc = {};
    // handle misc data
    dynamicInputs.forEach(item => fileItem.misc[item.apiValue] = response[item.apiValue]);
    fileItem.original = response;
    return fileItem;
  }

  /**
   * Concerts an ExcFileItem to an api response object
   * @param excFileItem
   */
  // TODO
  //  bad design decision,.the EXCFileItem should have the same property like the API response ...
  //  the converting is bad..
  public static toApiResponse(excFileItem: ExcFileItem, dynamicInputs: DynamicInputsInterface[]) {
    const response = {};
    response['ID'] = excFileItem.id;
    response['FKID'] = excFileItem.fkid;
    response['UPT'] = excFileItem.upt;
    response['CRU'] = excFileItem.owner;
    response['CRD'] = excFileItem.createdAt;
    response['FileName'] = excFileItem.fullName;
    response['MIME'] = excFileItem.type;
    response['Size'] = excFileItem.size;
    response['Label'] = excFileItem.misc.Label;
    // handle misc data
    dynamicInputs.forEach(item => response[item.apiValue] = excFileItem.misc[item.apiValue])
    return response;
  }

  /**
   * Creates an ExcFileItem instance from a file
   * @param file - file that provides the source data
   * @param misc - data that should saved as misc data
   */
  public static fromFile(file: File, misc ?: any) {
    const fileItem = new ExcFileItem();
    fileItem.isLoading = true;

    // set meta information
    fileItem.fullName = file.name;
    const nameParts = fileItem.fullName.split('.');
    fileItem.extension = nameParts.pop();
    fileItem.name = nameParts.join('.');
    fileItem.size = file.size;
    fileItem.type = file.type;

    // load file data
    const fileReader = new FileReader();
    fileReader.onload = () => {
      fileItem.data = fileReader.result as ArrayBuffer;
      fileItem.isLoading = false;
      fileItem._afterLoading.next(fileItem);
      fileItem._afterLoading.complete();
    }
    fileReader.readAsArrayBuffer(file);

    // set misc data
    fileItem.misc = misc;

    return fileItem;
  }

  /**
   * Returns a deep copy of this instance
   */
  getCopy(): ExcFileItem {
    const copy = new ExcFileItem();
    // copy all properties
    Object.keys(this).forEach(key => copy[key] = this[key]);
    // make a deep copy of data
    if (this.data) {
      copy.data = this.data.slice(0);
    }
    // set some vars to default value
    copy.isLoading = false;
    return copy;
  }

  /**
   * Creates a FormData object from an array of ExcFileItems. Consider that only the misc properties of the
   * first ExcFileItems are used, whereas the blob value is used from each ExcFileItem.
   * @param excFileItems
   */
  static toFormData(excFileItems: ExcFileItem[]): FormData {
    const formData = new FormData();
    // set all misc values.
    Object.keys(excFileItems[0].misc).forEach(key => formData.append(key, excFileItems[0].misc[key]));
    // set file values
    excFileItems.forEach(item => formData.append('file[]', item.asBlob, item.fullName));
    return formData;
  }
}
